import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { environment } from '@environments/environment';
import { ENVIRONMENTS } from '@shared/constants';

type DotLottie = import('@lottiefiles/dotlottie-web').DotLottie;

export const LOTTIE_TEST_HANDLE = '_dotLottie' as const;
type LottieHost = HTMLElement & { [LOTTIE_TEST_HANDLE]?: DotLottie };

@Directive({ selector: '[b2cLottie]' })
export class LottieDirective implements AfterViewInit, OnDestroy {
  loop = input<boolean>(true);
  src = input.required<string>();
  autoplay = input<boolean>(true);

  private destroyed = false;
  private dotLottie: DotLottie | null = null;
  private canvas: HTMLCanvasElement | null = null;

  private host = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);
  private document: Document = inject(DOCUMENT);

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const { DotLottie } = await import('@lottiefiles/dotlottie-web');

      if (this.destroyed) {
        return;
      }

      const canvas = this.document.createElement('canvas');
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      this.host.nativeElement.appendChild(canvas);
      this.canvas = canvas;

      this.dotLottie = new DotLottie({
        canvas,
        src: this.src(),
        loop: this.loop(),
        autoplay: this.autoplay(),
      });

      if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
        (this.host.nativeElement as LottieHost)._dotLottie = this.dotLottie;
      }
    } catch (error) {
      this.dotLottie?.destroy();
      this.dotLottie = null;
      this.canvas?.remove();
      this.canvas = null;
      if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
        delete (this.host.nativeElement as LottieHost)._dotLottie;
      }
      console.error('Failed to initialize DotLottie animation', error);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.dotLottie?.destroy();
    this.dotLottie = null;
    this.canvas?.remove();
    this.canvas = null;
    if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
      delete (this.host.nativeElement as LottieHost)._dotLottie;
    }
  }
}
